import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  Plus, 
  Calendar, 
  Search, 
  Filter,
  MessageSquare,
  Heart,
  Trash2,
  Edit,
  Eye,
  Bell,
  Settings
} from 'lucide-react';
import { JournalEntry } from '../../types';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';

export const Journal: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [moodFilter, setMoodFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));

  useEffect(() => {
    const fetchJournalEntries = async () => {
      if (!user) return;

      try {
        setLoading(true);
        
        // Fetch from regular journals collection
        const journalsQuery = query(
          collection(db, 'users', user.uid, 'journals'),
          orderBy('createdAt', 'desc')
        );
        const journalsSnap = await getDocs(journalsQuery);
        const regularJournals = journalsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          mood: doc.data().mood || 0,
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as JournalEntry[];

        // Fetch from prompted journals collection (morning/evening check-ins)
        const promptedQuery = query(
          collection(db, 'users', user.uid, 'promptedJournals'),
          orderBy('createdAt', 'desc')
        );
        const promptedSnap = await getDocs(promptedQuery);
        const promptedJournals = promptedSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          mood: doc.data().overallMood || doc.data().mood || 0,
          content: `${doc.data().promptType || 'Check-in'} - ${new Date(doc.data().createdAt?.toDate()).toLocaleDateString()}`,
          tags: [doc.data().promptType || 'check-in'],
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as JournalEntry[];

        // Combine and sort by date
        const allJournals = [...regularJournals, ...promptedJournals]
          .sort((a, b) => {
            const dateA = a.createdAt ? a.createdAt.getTime() : 0;
            const dateB = b.createdAt ? b.createdAt.getTime() : 0;
            return dateB - dateA;
          });
        
        setEntries(allJournals);
      } catch (error) {
        console.error('Error fetching journal entries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [user]);

  const handleDeleteEntry = async (entryId: string) => {
    if (!user || !confirm('Are you sure you want to delete this journal entry?')) return;

    try {
      await deleteDoc(doc(db, 'users', user.uid, 'journals', entryId));
      setEntries(prev => prev.filter(entry => entry.id !== entryId));
    } catch (error) {
      console.error('Error deleting journal entry:', error);
    }
  };

  const getMoodEmoji = (mood: number) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😟';
    return '😢';
  };

  const getMoodColor = (mood: number) => {
    if (mood >= 8) return 'text-green-600 bg-green-50';
    if (mood >= 6) return 'text-blue-600 bg-blue-50';
    if (mood >= 4) return 'text-yellow-600 bg-yellow-50';
    if (mood >= 2) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesMood = moodFilter === 'all' || 
                       (moodFilter === 'high' && entry.mood >= 7) ||
                       (moodFilter === 'medium' && entry.mood >= 4 && entry.mood < 7) ||
                       (moodFilter === 'low' && entry.mood < 4);

    const entryMonth = entry.createdAt ? format(entry.createdAt, 'yyyy-MM') : '';
    const matchesMonth = selectedMonth === 'all' || entryMonth === selectedMonth;

    return matchesSearch && matchesMood && matchesMonth;
  });

  const avgMood = entries.length > 0 
    ? entries.reduce((sum, entry) => sum + entry.mood, 0) / entries.length 
    : 0;

  const thisMonthEntries = entries.filter(entry => 
    entry.createdAt && 
    entry.createdAt >= startOfMonth(new Date()) && 
    entry.createdAt <= endOfMonth(new Date())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6 w-full px-3 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Personal Journal</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Track your daily wellness and reflect on your experiences
          </p>
        </div>
        <Link
          to="/journal/new"
          className="mt-2 sm:mt-0 inline-flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
        >
          <Plus className="w-5 h-5" />
          <span>New Entry</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Entries</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{entries.length}</p>
            </div>
            <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{thisMonthEntries.length}</p>
            </div>
            <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Average Mood</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {avgMood > 0 ? avgMood.toFixed(1) : '--'}/10
              </p>
            </div>
            <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Notification Settings Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Bell className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                Journal Reminders
              </h3>
              <p className="text-sm text-gray-600">
                Get notified to complete your Morning and Evening Reflections. Stay consistent with your wellness journey!
              </p>
            </div>
          </div>
          <Link
            to="/journal/notifications"
            className="inline-flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <Settings className="w-4 h-4" />
            <span>Manage Notifications</span>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            {/* Month Filter */}
            <div className="flex items-center justify-between sm:justify-start gap-2">
              <Calendar className="text-gray-400 w-5 h-5" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              >
                <option value="all">All Months</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(i);
                  const value = format(date, 'yyyy-MM');
                  return (
                    <option key={value} value={value}>
                      {format(date, 'MMMM yyyy')}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Mood Filter */}
            <div className="flex items-center justify-between sm:justify-start gap-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                value={moodFilter}
                onChange={(e) => setMoodFilter(e.target.value as 'all' | 'high' | 'medium' | 'low')}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              >
                <option value="all">All Moods</option>
                <option value="high">High (7-10)</option>
                <option value="medium">Medium (4-6)</option>
                <option value="low">Low (1-3)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Entries */}
       {filteredEntries.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
          <MessageSquare className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            {entries.length === 0 ? 'No journal entries yet' : 'No entries match your filters'}
          </h3>
          <p className="text-sm sm:text-base text-gray-500 mb-6">
            {entries.length === 0 
              ? 'Start your wellness journey by writing your first journal entry'
              : 'Try adjusting your search or filter criteria'
            }
          </p>
          {entries.length === 0 && (
            <Link
              to="/journal/new"
              className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Write First Entry
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {filteredEntries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`text-xl sm:text-2xl p-2 rounded-lg ${getMoodColor(entry.mood)}`}>
                    {getMoodEmoji(entry.mood)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      Mood: {entry.mood}/10
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {entry.createdAt && format(entry.createdAt, 'EEEE, MMMM d, yyyy • h:mm a')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-2">
                  <Link
                    to={`/journal/${entry.id}`}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/journal/${entry.id}/edit`}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDeleteEntry(entry.id)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm sm:text-base text-gray-700 line-clamp-3">
                  {entry.content}
                </p>
              </div>

              {entry.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {entry.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
